import { useNavigate } from 'react-router-dom'
import EntitiesSort from './EntitiesSort'
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from 'ui/InputSelect'
import { Sort, FeedbackCategory, FeedbackStatus } from 'util/constants'
import AddButton from 'ui/AddButton'
import { useUser } from 'util/UserContext'

interface Props {
    sort: Sort
    searchParams: URLSearchParams
    onAddFeedbackClick: () => void
}

const FeedbackListSelector = ({ sort, searchParams, onAddFeedbackClick }: Props) => {
    const navigate = useNavigate()
    const { currentUser, setShowAuthModal } = useUser()
    const categoryFilter = searchParams.get('feedbackCategory') || 'All'
    const statusFilter = searchParams.get('feedbackStatus') || 'All'

    const handleSortChange = (newSort: Sort) => {
        const newSearchParams = new URLSearchParams(searchParams)
        navigate(`/user-feedback/${newSort}?${newSearchParams.toString()}`)
    }

    const handleCategoryChange = (newCategory: FeedbackCategory) => {
        const newSearchParams = new URLSearchParams(searchParams)
        newSearchParams.set('feedbackCategory', newCategory ?? 'Category')
        navigate(`/user-feedback/${sort}?${newSearchParams.toString()}`)
    }

    const handleStatusChange = (newStatus: FeedbackStatus) => {
        const newSearchParams = new URLSearchParams(searchParams)
        newSearchParams.set('feedbackStatus', newStatus ?? 'Status')
        navigate(`/user-feedback/${sort}?${newSearchParams.toString()}`)
    }

    const handleAddFeedbackClick = () => {
        if (currentUser) {
            onAddFeedbackClick()
        } else {
            setShowAuthModal(true)
        }
    }

    return (
        <div className="w-full">
            <div className="flex flex-col space-y-1 mb-1">
                <div className="flex mt-2 mx-2">
                    <AddButton
                        onClick={handleAddFeedbackClick}
                        tooltipTitle="Add Feedback"
                        buttonText="Feedback"
                        className="add-button w-full"
                    />
                </div>
            </div>
            <div className="flex gap-1 my-2 mx-2 md:mx-4 items-center">
                <EntitiesSort entityType="feedback" sort={sort} onSortChange={handleSortChange} />
                <Select value={categoryFilter && categoryFilter !== 'All' ? categoryFilter : 'All'} onValueChange={(value) => handleCategoryChange(value as FeedbackCategory)}>
                    <SelectTrigger className="px-3 py-2">
                        <SelectValue placeholder="Category" />
                    </SelectTrigger>
                    <SelectContent>
                        {categoryFilter && categoryFilter !== 'All' ? <SelectItem value="All">Any Category</SelectItem> : 
                            <SelectItem value="All">Category</SelectItem>}
                        <SelectItem value="Bug">Bug</SelectItem>
                        <SelectItem value="Enhancement">Enhancement</SelectItem>
                        <SelectItem value="Functionality">Functionality</SelectItem>
                        <SelectItem value="User Experience (UX)">Experience (UX)</SelectItem>
                        <SelectItem value="User Interface (UI)">Interface (UI)</SelectItem>
                        <SelectItem value="Performance">Performance</SelectItem>
                        <SelectItem value="Compliance">Compliance</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={statusFilter && statusFilter !== '' ? statusFilter : 'All'} onValueChange={(value) => handleStatusChange(value as FeedbackStatus)}>
                    <SelectTrigger className="px-3 py-2">
                        <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                        {statusFilter && statusFilter !== 'All' ? <SelectItem value="All">Any Status</SelectItem> : 
                            <SelectItem value="All">Status</SelectItem>}
                        <SelectItem value="Open">Open</SelectItem>
                        <SelectItem value="Reviewing">Reviewing</SelectItem>
                        <SelectItem value="Planned">Planned</SelectItem>
                        <SelectItem value="Unplanned">Unplanned</SelectItem>
                        <SelectItem value="Developing">Developing</SelectItem>
                        <SelectItem value="Released">Released</SelectItem>
                        <SelectItem value="Closed">Closed</SelectItem>
                    </SelectContent>
                </Select>
            </div>
        </div>
    )
}

export default FeedbackListSelector